import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import AddItemModal from '../inventory/AddItemModal';
import { inventoryAPI } from '../../services/inventoryAPI';

interface SerializedItemBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseId: string;
  lineItemId?: string; // NEW: Optional line item ID
  purchaseDate: string;
  subcategoryId: string;
  subcategoryName: string;
  suggestedQuantity: number;
  onComplete: (createdItemIds: string[]) => void;
}

const SerializedItemBatchModal: React.FC<SerializedItemBatchModalProps> = ({
  isOpen,
  onClose,
  purchaseId,
  lineItemId, // NEW
  purchaseDate,
  subcategoryId,
  subcategoryName,
  suggestedQuantity,
  onComplete
}) => {
  const [quantity, setQuantity] = useState<string>(suggestedQuantity.toString());
  const [isCreatingItems, setIsCreatingItems] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [createdItemIds, setCreatedItemIds] = useState<string[]>([]);
  const [error, setError] = useState<string>('');

  const totalItems = parseInt(quantity) || 0;
  const isValidQuantity = totalItems > 0 && totalItems <= 100; // Max 100 items at once

  const handleStart = () => {
    if (!isValidQuantity) {
      setError('Please enter a valid quantity between 1 and 100');
      return;
    }
    setError('');
    setIsCreatingItems(true);
    setCurrentItemIndex(0);
    setCreatedItemIds([]);
  };

 const handleItemSave = async (itemData: any) => {
    try {
      // Actually create the item via API
      const response = await inventoryAPI.createItem(itemData);
      
      if (!response.success || !response.data) {
        setError(`Failed to create item ${currentItemIndex + 1}: ${response.error?.message || 'Unknown error'}`);
        return;
      }

      // Track created item ID
      const newCreatedIds = [...createdItemIds, response.data.itemId];
      setCreatedItemIds(newCreatedIds);

      // Check if we're done
      if (currentItemIndex < totalItems - 1) {
        // More items to create
        setCurrentItemIndex(prev => prev + 1);
      } else {
        // All items created - complete the batch
        setIsCreatingItems(false);
        onComplete(newCreatedIds);
        handleClose();
      }
    } catch (err: any) {
      setError(`Error creating item ${currentItemIndex + 1}: ${err.message}`);
    }
  };

  const handleCancel = () => {
    if (createdItemIds.length > 0) {
      // Partial completion - still call onComplete with what we have
      onComplete(createdItemIds);
    }
    handleClose();
  };

  const handleClose = () => {
    setQuantity(suggestedQuantity.toString());
    setIsCreatingItems(false);
    setCurrentItemIndex(0);
    setCreatedItemIds([]);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Show AddItemModal when creating items
  if (isCreatingItems) {
    return (
     <AddItemModal
        key={`item-${currentItemIndex}`}
        isOpen={true}
        onClose={handleCancel}
        onSave={handleItemSave}
        purchaseId={purchaseId}
        lineItemId={lineItemId} // NEW: Pass line item ID
        suggestedCategory={subcategoryId}
        suggestedItemType="SERIALIZED"
        suggestedPurchaseDate={purchaseDate}
        titleOverride={`Add Item ${currentItemIndex + 1} of ${totalItems} - ${subcategoryName}`}
      />
    );
  }

  // Show quantity input modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Create Serialized Items</h2>
            <p className="text-sm text-gray-600 mt-1">
              {subcategoryName}
            </p>
          </div>
          <button 
            onClick={handleClose} 
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Purchase ID:</strong> {purchaseId}
            </p>
            {lineItemId && (
              <p className="text-sm text-blue-800 mt-1">
                <strong>Line Item ID:</strong> {lineItemId}
              </p>
            )}
            <p className="text-sm text-blue-800 mt-1">
              Each item will be tracked individually with a unique serial number.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How many items to create? <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter quantity"
            />
            {error && (
              <div className="flex items-center space-x-2 mt-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              You'll enter a serial number for each item (maximum 100 at once)
            </p>
          </div>

          {isValidQuantity && totalItems > 1 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <strong>Process:</strong> The form will loop {totalItems} times. 
                You'll enter details for each item individually.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={!isValidQuantity}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Creating Items
          </button>
        </div>
      </div>
    </div>
  );
};

export default SerializedItemBatchModal;
